import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { siteBuilderAPI, mediaAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { FiSave, FiPlus, FiTrash2, FiImage, FiExternalLink, FiGrid, FiX, FiCheck, FiLoader, FiUpload } from 'react-icons/fi';
import ImageUploader from '../../components/ImageUploader';

export default function PartnerLogosPage() {
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partnerLogos, setPartnerLogos] = useState([]);

  useEffect(() => {
    loadSite();
  }, []);

  const loadSite = async () => {
    try {
      const response = await siteBuilderAPI.getMySite();
      setSite(response.data);
      // Load partner logos from site - check both locations
      const logos = response.data.site_settings?.client_logos || response.data.partner_logos || [];
      // Ensure at least 8 empty slots
      while (logos.length < 8) {
        logos.push({ name: '', url: '', link: '' });
      }
      setPartnerLogos(logos);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out empty logos (no name and no url)
      const logosToSave = partnerLogos.filter(logo => logo.name || logo.url);
      
      // Save to both locations for compatibility
      await siteBuilderAPI.updateSite({
        ...site,
        partner_logos: logosToSave,
        site_settings: {
          ...site.site_settings,
          client_logos: logosToSave
        }
      });
      toast.success('Logos partenaires enregistrés !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateLogo = (index, field, value) => {
    const updated = [...partnerLogos];
    updated[index] = { ...updated[index], [field]: value };
    setPartnerLogos(updated);
  };

  const addLogo = () => {
    setPartnerLogos([...partnerLogos, { name: '', url: '', link: '' }]);
  };

  const removeLogo = (index) => {
    setPartnerLogos(partnerLogos.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiLoader className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Logos Partenaires</h1>
          <p className="text-slate-500 mt-1">
            Gérez les logos qui s'affichent dans la bannière "Ils nous font confiance"
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saving ? <FiLoader className="animate-spin" /> : <FiSave />}
          Enregistrer
        </Button>
      </div>

      {/* Preview Banner */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-lg">Aperçu de la bannière</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-xs uppercase tracking-wider text-center mb-4">
            Ils nous font confiance
          </p>
          <div className="flex gap-8 overflow-x-auto py-2 items-center justify-center flex-wrap">
            {partnerLogos.filter(l => l.url || l.name).slice(0, 8).map((logo, i) => (
              <div key={i} className="flex-shrink-0 group">
                {logo.url ? (
                  <div className="relative h-12 w-32 flex items-center justify-center overflow-hidden">
                    {/* Colored version (shown on hover) */}
                    <img 
                      src={logo.url} 
                      alt={logo.name} 
                      className="absolute h-full w-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    {/* Golden version (default) */}
                    <img 
                      src={logo.url} 
                      alt={logo.name} 
                      className="h-full w-full object-contain opacity-60 group-hover:opacity-0 transition-opacity duration-300"
                      style={{ filter: 'sepia(100%) saturate(300%) brightness(90%) hue-rotate(5deg)' }}
                      onError={(e) => { 
                        e.target.style.display = 'none';
                        const span = e.target.parentElement.querySelector('span');
                        if (span) span.style.display = 'inline';
                      }}
                    />
                    <span className="text-amber-500 font-bold text-sm hidden absolute">
                      {logo.name}
                    </span>
                  </div>
                ) : (
                  <span className="text-amber-500 font-bold text-sm border border-amber-500/30 rounded px-3 py-1 opacity-60 hover:opacity-100 transition-opacity">
                    {logo.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Logos Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiGrid className="h-5 w-5" />
            Logos ({partnerLogos.length})
          </CardTitle>
          <CardDescription>
            Ajoutez les logos de vos partenaires, clients ou références
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {partnerLogos.map((logo, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border rounded-lg p-4 space-y-3 relative group"
              >
                <button
                  onClick={() => removeLogo(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <FiX className="h-3 w-3" />
                </button>

                <div className="text-xs font-medium text-slate-500 mb-2">
                  Logo #{index + 1}
                </div>

                <div>
                  <Label className="text-xs">Nom du partenaire</Label>
                  <Input
                    value={logo.name || ''}
                    onChange={(e) => updateLogo(index, 'name', e.target.value)}
                    placeholder="Ex: Entreprise XYZ"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs">Logo (Image)</Label>
                  <ImageUploader
                    value={logo.url || ''}
                    onChange={(url) => updateLogo(index, 'url', url)}
                    showLibrary={true}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs">Lien (optionnel)</Label>
                  <Input
                    value={logo.link || ''}
                    onChange={(e) => updateLogo(index, 'link', e.target.value)}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>
              </motion.div>
            ))}
          </div>

          <Button 
            variant="outline" 
            onClick={addLogo}
            className="w-full mt-4 gap-2"
          >
            <FiPlus />
            Ajouter un logo
          </Button>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-amber-800 mb-2">💡 Conseils</h3>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>• Utilisez des logos en format PNG avec fond transparent pour un meilleur rendu</li>
            <li>• Les logos seront affichés en blanc sur fond sombre (filtre appliqué automatiquement)</li>
            <li>• Recommandation : logos d'au moins 200px de large pour une bonne qualité</li>
            <li>• Le lien est optionnel mais permet de rediriger vers le site du partenaire</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
